import ShareModal from "@/src/components/action-modals/share-modal";
import { BlocksApiArticle } from "@/src/models/wiki.models";
import { resolveAuthorName } from "@/src/services/wiki/wiki.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { router } from "expo-router";
import { PictureFrame } from "iconsax-react-nativejs";
import { useState } from "react";
import {
    Image,
    Platform,
    Pressable,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import DropDown from "../../ui-kit/drop-down";
import BlocksAvatar from "../../ui-kit/blocks-avatar";

type DropdownItem = {
  label: string;
  onPress: () => void;
  destructive?: boolean;
};

export type WikiArticleCardProps = Readonly<{
  article: BlocksApiArticle;
  itemWidthClass: string;
  dropdownItems: DropdownItem[];
  onPress?: (article: BlocksApiArticle) => void;
  showShareModal?: boolean;
  onShareModalClose?: () => void;
  emptyTitleLabel?: string;
}>;

export default function WikiArticleCard({
  article,
  itemWidthClass,
  dropdownItems,
  onPress,
  showShareModal = false,
  onShareModalClose,
  emptyTitleLabel,
}: WikiArticleCardProps) {
  const { themeVars } = useTheme();
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isCardPressed, setIsCardPressed] = useState(false);

  const isWeb = Platform.OS === "web";
  const isHighlighted = isWeb ? isCardHovered : isCardPressed;

  const authorName = resolveAuthorName(article);
  const formattedDate = article.CreatedDate
    ? new Date(article.CreatedDate).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "";

  const handlePress = () => {
    if (onPress) {
      onPress(article);
    } else {
      router.push({
        pathname: "/(tabs)/wiki/wiki-article-detail",
        params: { articleId: article.ItemId },
      });
    }
  };

  return (
    <>
      {showShareModal && onShareModalClose && (
        <ShareModal
          visible={showShareModal}
          onClose={onShareModalClose}
          resourceId={article.ItemId}
          resourcePath="wiki"
          title={article.Title}
        />
      )}

      <Pressable
        onPress={handlePress}
        className={`${itemWidthClass} border bg-chalk rounded-2xl mb-4`}
        onHoverIn={() => setIsCardHovered(true)}
        onHoverOut={() => setIsCardHovered(false)}
        onPressIn={() => !isWeb && setIsCardPressed(true)}
        onPressOut={() => !isWeb && setIsCardPressed(false)}
        style={{
          borderColor: isHighlighted
            ? themeVars["--color-primary-default"]
            : themeVars["--color-ash-3"],
        }}
      >
        <View className="px-1 pt-1 h-32 items-center justify-center overflow-hidden">
          {article.coverImageUrl?.trim() ? (
            <Image
              className="rounded-lg w-full h-full"
              source={{ uri: article.coverImageUrl }}
              resizeMode="cover"
            />
          ) : (
            <View className="flex-1 w-full rounded-lg bg-obsidian items-center justify-center">
              <PictureFrame className="text-onyx" size={30} />
            </View>
          )}
        </View>

        <View className="flex-row justify-between px-2 py-2">
          <View className="flex-row gap-2 flex-1">
            <BlocksAvatar
              userId={article.CreatedBy}
              fallbackImageUrl={article.authorSnapshot?.profileImageUrl}
              size={30}
              className="rounded-full"
            />
            <View className="flex-col flex-1">
              <Text className="font-medium text-sm text-onyx" numberOfLines={1}>
                {authorName}
              </Text>
            </View>
          </View>

          <TouchableOpacity
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
            className="justify-center"
          >
            <DropDown items={dropdownItems} />
          </TouchableOpacity>
        </View>

        <View className="px-2">
          <Text className="font-semibold text-sm text-onyx" numberOfLines={2}>
            {article.Title ?? (
              <Text className="italic text-ash-1">{emptyTitleLabel}</Text>
            )}
          </Text>
        </View>

        <View className="flex-row gap-2 items-center px-2 mb-2 mt-1">
          {formattedDate && (
            <Text className="text-xs text-ash-1">{formattedDate}</Text>
          )}
        </View>
      </Pressable>
    </>
  );
}
